import React from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { FaShoppingCart, FaTicketAlt, FaMoneyBillWave, FaCalendarAlt, FaChartLine, FaUsers, FaTrophy } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/dashboard.css';

const Dashboard = () => {
  const { user, hasRole } = useAuth();
  
  // Dados simulados para o dashboard
  const dashboardData = {
    orderStats: {
      total: 5,
      pending: 2,
      approved: 3
    },
    payments: {
      total: 15500,
      paid: 9800,
      pending: 5700
    },
    upcomingEvents: [
      {
        id: 1,
        name: 'Congresso Nacional de Medicina',
        date: '15/07/2025',
        location: 'São Paulo Expo',
        status: 'confirmed'
      },
      {
        id: 2,
        name: 'Feira de Tecnologia MedTech',
        date: '20/08/2025',
        location: 'Expo Center Norte',
        status: 'pending'
      }
    ]
  };
  
  // Componente de card de estatísticas moderno
  const ModernStatsCard = ({ title, value, icon, color, footer, delay = 0 }) => (
    <div 
      className="dashboard-stats-card"
      style={{
        '--card-color': color,
        '--animation-delay': `${delay}s`
      }}
    >
      <div className="dashboard-icon">
        {icon}
      </div>
      <div className="dashboard-content">
        <h3 className="dashboard-value">{value}</h3>
        <p className="dashboard-title">{title}</p>
        {footer && (
          <div className="dashboard-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
  
  // Formatação de valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaChartLine className="dashboard-title-icon" />
              Dashboard Principal
            </h1>
            <p className="dashboard-subtitle">
              Visão geral das principais métricas e atividades do sistema
            </p>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">
            <FaTrophy className="dashboard-section-icon" />
            Métricas Principais
          </h2>
          <p className="dashboard-section-subtitle">
            Acompanhe os indicadores mais importantes do seu negócio
          </p>
        </div>

        <div className="dashboard-stats-grid">
          <ModernStatsCard 
            title="Total de Pedidos" 
            value={dashboardData.orderStats.total}
            icon={<FaShoppingCart />}
            color="#6f42c1"
            delay={0}
            footer={
              <div className="stats-badges">
                <Badge bg="success" className="me-2">
                  {dashboardData.orderStats.approved}
                </Badge>
                <span className="badge-label">Aprovados</span>
                <Badge bg="warning" className="mx-2">
                  {dashboardData.orderStats.pending}
                </Badge>
                <span className="badge-label">Pendentes</span>
              </div>
            }
          />
          
          <ModernStatsCard 
            title="Próximos Eventos" 
            value={dashboardData.upcomingEvents.length}
            icon={<FaCalendarAlt />}
            color="#28a745"
            delay={0.1}
            footer={
              <span className="next-event">Próximo: {dashboardData.upcomingEvents[0].date}</span>
            }
          />
          
          <ModernStatsCard 
            title="Total a Pagar" 
            value={formatCurrency(dashboardData.payments.total)}
            icon={<FaMoneyBillWave />}
            color="#ffc107"
            delay={0.2}
            footer={
              <span className="payment-info">Pago: {formatCurrency(dashboardData.payments.paid)}</span>
            }
          />
          
          <ModernStatsCard 
            title="Credenciais" 
            value="12"
            icon={<FaTicketAlt />}
            color="#dc3545"
            delay={0.3}
            footer={
              <span className="credentials-info">Disponíveis para download</span>
            }
          />
        </div>
        
        {/* Tables Section */}
        <Row className="dashboard-tables-row">
          {/* Seção de próximos eventos */}
          <Col lg={6}>
            <Card className="modern-table-card">
              <Card.Header className="modern-card-header">
                <h5 className="modern-card-title">
                  <FaCalendarAlt className="card-title-icon" />
                  Próximos Eventos
                </h5>
              </Card.Header>
              <Card.Body className="modern-card-body">
                <div className="modern-table-container">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Evento</th>
                        <th>Data</th>
                        <th>Local</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.upcomingEvents.map(event => (
                        <tr key={event.id}>
                          <td className="event-name">{event.name}</td>
                          <td className="event-date">{event.date}</td>
                          <td className="event-location">{event.location}</td>
                          <td>
                            <Badge 
                              bg={event.status === 'confirmed' ? 'success' : 'warning'}
                              className="modern-badge"
                            >
                              {event.status === 'confirmed' ? 'Confirmado' : 'Pendente'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Seção de pedidos recentes */}
          <Col lg={6}>
            <Card className="modern-table-card">
              <Card.Header className="modern-card-header">
                <h5 className="modern-card-title">
                  <FaShoppingCart className="card-title-icon" />
                  Pedidos Recentes
                </h5>
              </Card.Header>
              <Card.Body className="modern-card-body">
                <div className="modern-table-container">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Pedido</th>
                        <th>Data</th>
                        <th>Valor</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="order-id">#1002</td>
                        <td className="order-date">25/06/2025</td>
                        <td className="order-value">{formatCurrency(10430)}</td>
                        <td>
                          <Badge bg="warning" className="modern-badge">
                            Em Análise
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="order-id">#1001</td>
                        <td className="order-date">24/06/2025</td>
                        <td className="order-value">{formatCurrency(5390)}</td>
                        <td>
                          <Badge bg="success" className="modern-badge">
                            Aprovado
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* Seção adicional apenas para admins */}
        {hasRole('admin') && (
          <div className="admin-section">
            <div className="dashboard-section-header">
              <h2 className="dashboard-section-title">
                <FaUsers className="dashboard-section-icon" />
                Painel Administrativo
              </h2>
              <p className="dashboard-section-subtitle">
                Métricas exclusivas para administradores do sistema
              </p>
            </div>

            <Card className="admin-panel-card">
              <Card.Header className="modern-card-header">
                <h5 className="modern-card-title">
                  <FaTrophy className="card-title-icon" />
                  Indicadores Administrativos
                </h5>
              </Card.Header>
              <Card.Body className="modern-card-body">
                <Row>
                  <Col md={4} className="mb-3">
                    <div className="admin-metric-card">
                      <div className="metric-icon">
                        <FaUsers />
                      </div>
                      <div className="metric-content">
                        <h6 className="metric-title">Total de Expositores</h6>
                        <h2 className="metric-value">24</h2>
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="mb-3">
                    <div className="admin-metric-card">
                      <div className="metric-icon">
                        <FaShoppingCart />
                      </div>
                      <div className="metric-content">
                        <h6 className="metric-title">Pedidos Aguardando</h6>
                        <h2 className="metric-value">7</h2>
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="mb-3">
                    <div className="admin-metric-card">
                      <div className="metric-icon">
                        <FaMoneyBillWave />
                      </div>
                      <div className="metric-content">
                        <h6 className="metric-title">Faturamento Total</h6>
                        <h2 className="metric-value">{formatCurrency(237850)}</h2>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;